import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ContactModal from "../components/ContactModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Calendar, 
  CheckCircle, 
  ArrowRight, 
  Zap, 
  Users, 
  Target, 
  TrendingUp, 
  Shield,
  Award,
  Lightbulb,
  Settings,
  BarChart3,
  Code,
  Database,
  Lock,
  Globe,
  Brain,
  Cpu,
  Network,
  Sparkles
} from "lucide-react";

const seoConfig = {
  title: "KI-Plattform | Enterprise AI Platform | BROVKO",
  description: "Die sichere KI-Plattform für Ihr Unternehmen. Private GPT-Umgebung, DSGVO-konform, skalierbar und nahtlos integrierbar.",
  keywords: "KI-Plattform, Enterprise AI, Private GPT, DSGVO KI, AI Platform, Sichere KI",
  canonicalUrl: "/leistungsangebot/ki-plattform"
};

const KIPlattform = () => {
  const { t } = useLanguage();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  const platformFeatures = [
    {
      icon: Lock,
      title: "Private GPT-Umgebung",
      subtitle: "100% Datenschutz für Ihr Unternehmen",
      features: [
        "Eigene GPT-Instanz ohne Datenübertragung nach außen",
        "Vollständige DSGVO-Konformität und Compliance",
        "On-Premise oder Private Cloud Deployment",
        "Komplette Kontrolle über Ihre Unternehmensdaten"
      ],
      benefits: ["100% Datenschutz", "DSGVO-konform", "Compliance-sicher"]
    },
    {
      icon: Brain,
      title: "Neueste LLM-Technologien",
      subtitle: "Zugriff auf die besten KI-Modelle",
      features: [
        "GPT von OpenAI (neueste Versionen)",
        "Claude Sonnet von Anthropic",
        "Gemini von Google",
        "DeepSeek und weitere Top-LLMs",
        "Automatische Updates auf neueste Modelle"
      ],
      benefits: ["Cutting-Edge AI", "Modell-Vielfalt", "Stets aktuell"]
    },
    {
      icon: Users,
      title: "Multi-Agenten-System",
      subtitle: "Von einzelnen Assistenten bis zu komplexen Systemen",
      features: [
        "GPT-Assistent für jeden Mitarbeiter",
        "Spezialisierte Agenten für verschiedene Aufgaben",
        "Multi-Agenten-Workflows für komplexe Prozesse",
        "Rollenbasierte KI-Unterstützung"
      ],
      benefits: ["Skalierbar", "Flexibel", "Individuell"]
    },
    {
      icon: TrendingUp,
      title: "Unternehmensoptimierung",
      subtitle: "Produktivität und Effizienz maximieren",
      features: [
        "Prozessoptimierung durch KI-Analyse",
        "Datenauswertung und Business Intelligence",
        "Kostensenkung durch Automatisierung",
        "Innovation und neue Produktentwicklung"
      ],
      benefits: ["Höhere Produktivität", "Kosteneinsparung", "Innovation"]
    }
  ];

  const whyAIWizz = [
    {
      icon: Shield,
      title: "Maximaler Datenschutz",
      description: "Ihre Daten bleiben zu 100% in Ihrem Unternehmen - keine Übertragung an Dritte"
    },
    {
      icon: Sparkles,
      title: "Neueste KI-Technologie",
      description: "Zugriff auf die aktuellsten und besten LLM-Modelle am Markt"
    },
    {
      icon: Users,
      title: "Unternehmensweite Befähigung",
      description: "Alle Mitarbeiter werden produktiver, effektiver und smarter durch KI"
    },
    {
      icon: Settings,
      title: "Vollständige Integration",
      description: "Nahtlose Einbindung in bestehende Unternehmensprozesse und -systeme"
    },
    {
      icon: Target,
      title: "Messbare Ergebnisse",
      description: "Konkrete Verbesserungen in Produktivität, Effizienz und Kosteneinsparung"
    },
    {
      icon: Network,
      title: "Skalierbare Architektur",
      description: "Von einzelnen Assistenten bis zu komplexen Multi-Agenten-Systemen"
    }
  ];

  const useCases = [
    {
      department: "Management & Führung",
      icon: Target,
      applications: [
        "Strategische Entscheidungsunterstützung",
        "Business Intelligence und Reporting",
        "Marktanalyse und Competitive Intelligence",
        "Performance Monitoring und KPI-Tracking"
      ],
      impact: "40% schnellere Entscheidungen"
    },
    {
      department: "Sales & Marketing",
      icon: TrendingUp,
      applications: [
        "Lead-Generierung und -Qualifizierung",
        "Personalisierte Kundenansprache",
        "Content-Erstellung und Kampagnen-Optimierung",
        "Sales Forecasting und Pipeline-Management"
      ],
      impact: "30% höhere Conversion-Rate"
    },
    {
      department: "HR & Personal",
      icon: Users,
      applications: [
        "Intelligente Bewerbervorauswahl",
        "Mitarbeiterentwicklung und Skill-Matching",
        "Automatisierte HR-Prozesse",
        "Employee Experience Optimierung"
      ],
      impact: "50% weniger Recruiting-Zeit"
    },
    {
      department: "IT & Entwicklung",
      icon: Code,
      applications: [
        "Code-Generierung und Review",
        "Automatisierte Testing und Debugging",
        "Dokumentation und Knowledge Management",
        "System-Monitoring und Incident Response"
      ],
      impact: "60% schnellere Entwicklung"
    }
  ];

  const securityFeatures = [
    "End-to-End verschlüsselte Kommunikation",
    "On-Premise oder Private Cloud Hosting",
    "Rollenbasierte Zugriffskontrolle und Berechtigungen",
    "Vollständige Audit-Logs und Compliance-Reporting",
    "ISO 27001 und DSGVO konforme Implementierung",
    "Regelmäßige Sicherheitsaudits und Penetrationstests"
  ];

  const modelSupport = [
    { name: "OpenAI GPT", version: "Neueste Versionen", features: "Text, Code, Analyse" },
    { name: "Claude Sonnet", version: "Anthropic", features: "Reasoning, Creative Writing" },
    { name: "Google Gemini", version: "Multimodal", features: "Text, Bilder, Daten" },
    { name: "DeepSeek", version: "Open Source", features: "Code, Mathematik" },
    { name: "Weitere LLMs", version: "Nach Bedarf", features: "Spezialisierte Modelle" }
  ];

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <SEOHead {...seoConfig} />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <div className="inline-flex items-center gap-2 bg-teal-400/10 text-brand-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Brain className="h-4 w-4" />
              {t('kiPlattform.hero.badge')}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {t('kiPlattform.hero.title')}
            </h1>
            <p className="text-xl text-ink-muted mb-8 leading-relaxed">
              {t('kiPlattform.hero.description')}
            </p>
            <p className="text-lg text-ink-faint mb-12">
              {t('kiPlattform.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-8 py-4 text-lg font-semibold"
                onClick={() => openContactModal("Hero Section", t('kiPlattform.hero.cta'))}
              >
                <Calendar className="mr-2 h-5 w-5" />
                {t('kiPlattform.hero.cta')}
              </Button>
              <Button 
                variant="outline" 
                className="border-teal-400 text-brand-400 hover:bg-teal-400 hover:text-ink-onBrand px-8 py-4 text-lg font-semibold"
                onClick={() => window.open('https://www.aiwizz.ai/', '_blank')}
              >
                <ArrowRight className="mr-2 h-5 w-5" />
                {t('kiPlattform.hero.ctaSecondary')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why AIWizz Section */}
      <section className="py-20 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('kiPlattform.whyAIWizz.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {whyAIWizz.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="bg-teal-400/10 p-3 rounded-lg flex-shrink-0">
                      <IconComponent className="h-8 w-8 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white mb-3">{item.title}</h3>
                      <p className="text-ink-muted leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('kiPlattform.features.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {platformFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-8 border border-hairline hover:border-teal-400/50 transition-colors">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="bg-teal-400/10 p-3 rounded-lg">
                        <IconComponent className="h-8 w-8 text-brand-400" />
                      </div>
                      <div>
                        <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-white">{feature.title}</h3>
                        <p className="text-ink-faint mt-1">{feature.subtitle}</p>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {feature.features.map((feat, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-3">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-sm leading-relaxed">{feat}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="border-t border-hairline pt-4">
                      <div className="flex flex-wrap gap-2">
                        {feature.benefits.map((benefit, bIndex) => (
                          <span key={bIndex} className="bg-teal-400/10 text-brand-400 px-3 py-1 rounded-full text-xs font-medium">
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases by Department */}
      <section className="py-20 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-12 text-center">
              {t('kiPlattform.useCases.title')}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {useCases.map((useCase, index) => {
                const IconComponent = useCase.icon;
                return (
                  <div key={index} className="bg-surface-raised/50 rounded-lg p-6 border border-slate-700/30">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-teal-400/10 p-3 rounded-lg">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{useCase.department}</h3>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {useCase.applications.map((app, aIndex) => (
                        <li key={aIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                          <span className="text-ink-muted text-sm leading-relaxed">{app}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-brand-400" />
                      <span className="text-brand-400 text-sm font-medium">{useCase.impact}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Model Support */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiPlattform.models.title')}
            </h2>
            <p className="text-xl text-ink-faint mb-12 text-center">
              {t('kiPlattform.models.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modelSupport.map((model, index) => (
                <div key={index} className="bg-slate-800/30 rounded-lg p-6 border border-slate-700/30 text-center">
                  <div className="bg-teal-400/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Brain className="h-8 w-8 text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{model.name}</h3>
                  <p className="text-brand-400 text-sm font-medium mb-2">{model.version}</p>
                  <p className="text-ink-faint text-sm">{model.features}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 bg-surface-raised/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-8 text-center">
              {t('kiPlattform.security.title')}
            </h2>
            <p className="text-xl text-ink-faint mb-12 text-center">
              {t('kiPlattform.security.subtitle')}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {securityFeatures.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <Shield className="h-6 w-6 text-brand-400 mt-1 flex-shrink-0" />
                  <p className="text-ink-muted leading-relaxed">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-white mb-6">
              {t('kiPlattform.cta.title')}
            </h2>
            <p className="text-xl text-ink-muted mb-12">
              {t('kiPlattform.cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                className="bg-teal-400 hover:bg-brand text-ink-onBrand px-8 py-4 text-lg font-semibold"
                onClick={() => openContactModal("CTA Section", t('kiPlattform.hero.cta'))}
              >
                <Calendar className="mr-2 h-5 w-5" />
                {t('kiPlattform.hero.cta')}
              </Button>
              <Button 
                variant="outline" 
                className="border-teal-400 text-brand-400 hover:bg-teal-400 hover:text-ink-onBrand px-8 py-4 text-lg font-semibold"
                onClick={() => window.open('https://www.aiwizz.ai/', '_blank')}
              >
                <ArrowRight className="mr-2 h-5 w-5" />
                {t('kiPlattform.hero.ctaSecondary')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="KI-Plattform"
        buttonContext="AIWizz Demo"
        buttonText="AIWizz Demo buchen"
      />
    </div>
  );
};

export default KIPlattform;